// Relatório de um minicurso: indicadores, funil de etapas, questões, enquetes e alunos.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiDownload, FiEdit2 } from 'react-icons/fi';
import Carregando from '../Carregando';
import { Barras, Indicador, Medidor, SeloStatus } from './Graficos';
import { useEtapasDoCurso, useRespostas } from './useRelatorios';
import {
  concluiuCurso, estaParado, exportarExcel, formatarDataHora, formatarNota, formatarTelefone, paraData, pct,
  statusDoAluno, telefoneDoProgresso, tempoRelativo,
} from './util';

const LETRAS = ['A', 'B', 'C', 'D', 'E', 'F'];
const DISSERTATIVAS_INICIAIS = 8;

const tituloDaEtapa = (etapa) =>
  etapa.conteudo ? (etapa.tipoConteudo === 'pilula' ? etapa.conteudo.titulo : etapa.conteudo.enunciado) : 'Conteúdo removido';

function RelatorioCurso({ tenantId, curso, progressos, nomeDe, onVerAluno }) {
  const etapas = useEtapasDoCurso(curso.id);
  const respostas = useRespostas(tenantId, 'minicursoId', curso.id);
  const [verTodasDissertativas, setVerTodasDissertativas] = useState(false);

  const alunos = useMemo(
    () => progressos
      .filter((p) => p.minicursoId === curso.id)
      .sort((a, b) => (paraData(b.timestampUltimaEtapa)?.getTime() || 0) - (paraData(a.timestampUltimaEtapa)?.getTime() || 0)),
    [progressos, curso.id],
  );

  const resumo = useMemo(() => {
    const concluintes = alunos.filter(concluiuCurso);
    const aprovados = concluintes.filter((p) => p.statusAprovacao === 'Aprovado');
    const notas = concluintes.map((p) => p.notaFinal).filter((n) => typeof n === 'number');
    const quiz = (respostas || []).filter((r) => r.type === 'quiz');
    return {
      inscritos: alunos.length,
      concluintes: concluintes.length,
      aprovados: aprovados.length,
      notaMedia: notas.length ? notas.reduce((a, n) => a + n, 0) / notas.length : null,
      respostasQuiz: quiz.length,
      acertosQuiz: quiz.filter((r) => r.acertou === true).length,
      parados: alunos.filter(estaParado).length,
    };
  }, [alunos, respostas]);

  const questoes = useMemo(() => {
    if (!etapas || !respostas) return [];
    return etapas
      .filter((e) => e.tipoConteudo !== 'pilula' && e.conteudo?.type === 'quiz')
      .map((e) => {
        const q = e.conteudo;
        const rs = respostas.filter((r) => r.idquestao === q.id && r.type === 'quiz');
        const acertos = rs.filter((r) => r.acertou === true).length;
        const erros = {};
        rs.filter((r) => r.acertou === false).forEach((r) => {
          const letra = String(r.resposta || '').trim().toUpperCase();
          erros[letra] = (erros[letra] || 0) + 1;
        });
        const [letraErro, qtdErro] = Object.entries(erros).sort((a, b) => b[1] - a[1])[0] || [];
        const textoErro = letraErro ? q.options?.[LETRAS.indexOf(letraErro)]?.text : null;
        return {
          id: q.id, ordem: e.ordem, enunciado: q.enunciado, nivel: q.difficulty || '—',
          respostas: rs.length, acertos, taxa: pct(acertos, rs.length),
          erroComum: letraErro ? `${letraErro}${textoErro ? ` · ${textoErro}` : ''} (${qtdErro})` : '—',
        };
      })
      .sort((a, b) => (a.respostas === 0) - (b.respostas === 0) || a.taxa - b.taxa);
  }, [etapas, respostas]);

  const enquetes = useMemo(() => {
    if (!etapas || !respostas) return [];
    return etapas
      .filter((e) => e.tipoConteudo !== 'pilula' && e.conteudo && e.conteudo.type !== 'quiz')
      .map((e) => {
        const q = e.conteudo;
        const rs = respostas.filter((r) => r.idquestao === q.id);
        if (q.type === 'pesquisa_multipla_escolha') {
          const opcoes = (q.options || [])
            .map((o, i) => ({ letra: LETRAS[i], texto: o.text }))
            .filter((o) => o.texto && o.texto.trim());
          const itens = opcoes.map((o) => ({
            chave: o.letra, prefixo: o.letra, rotulo: o.texto,
            valor: rs.filter((r) => String(r.resposta || '').trim().toUpperCase() === o.letra).length,
          }));
          return { id: q.id, tipo: 'multipla', enunciado: q.enunciado, total: rs.length, itens };
        }
        const textos = rs
          .slice()
          .sort((a, b) => (paraData(b.timestamp)?.getTime() || 0) - (paraData(a.timestamp)?.getTime() || 0));
        return { id: q.id, tipo: 'dissertativa', enunciado: q.enunciado, total: rs.length, textos };
      });
  }, [etapas, respostas]);

  const totalEtapas = etapas ? etapas.length : 0;
  const nomeDoTelefone = useMemo(() => {
    const mapa = {};
    alunos.forEach((p) => { mapa[telefoneDoProgresso(p)] = nomeDe(p); });
    return mapa;
  }, [alunos, nomeDe]);

  const exportar = () => {
    exportarExcel(`relatorio-${curso.comando || curso.id}.xlsx`, [
      {
        nome: 'Alunos',
        linhas: alunos.map((p) => ({
          Aluno: nomeDe(p) || '',
          Telefone: formatarTelefone(telefoneDoProgresso(p), p.contactId),
          'Etapas concluídas': p.ultimaEtapaConcluida || 0,
          'Total de etapas': totalEtapas,
          Acertos: p.acertos || 0,
          Erros: p.erros || 0,
          Nota: typeof p.notaFinal === 'number' ? p.notaFinal : '',
          Status: statusDoAluno(p).rotulo,
          Tentativas: p.tentativas || 1,
          Inscrição: formatarDataHora(p.timestampInscricao),
          'Última atividade': formatarDataHora(p.timestampUltimaEtapa),
        })),
      },
      {
        nome: 'Questões',
        linhas: questoes.map((q) => ({
          Etapa: q.ordem, Questão: q.enunciado, Nível: q.nivel, Respostas: q.respostas,
          Acertos: q.acertos, '% de acerto': q.taxa, 'Erro mais comum': q.erroComum,
        })),
      },
      {
        nome: 'Respostas',
        linhas: (respostas || [])
          .slice()
          .sort((a, b) => (paraData(a.timestamp)?.getTime() || 0) - (paraData(b.timestamp)?.getTime() || 0))
          .map((r) => ({
            Aluno: nomeDoTelefone[r.telefone] || '',
            Telefone: formatarTelefone(r.telefone, r.idcontato),
            Questão: r.enunciado,
            Tipo: r.type === 'quiz' ? 'Quiz' : 'Pesquisa',
            Resposta: r.resposta,
            Gabarito: r.type === 'quiz' ? r.respostacorreta : '',
            Resultado: r.acertou === true ? 'Acertou' : r.acertou === false ? 'Errou' : '',
            Quando: formatarDataHora(r.timestamp),
          })),
      },
    ]);
  };

  if (!etapas || !respostas) return <Carregando texto="Carregando o relatório do curso..." />;

  return (
    <div className="rel-curso">
      <div className="rel-curso__acoes">
        <span className="rel-curso__comando">
          Comando no WhatsApp: <code>{curso.comando}</code> · {totalEtapas} etapas
        </span>
        <span className="rel-curso__botoes">
          <Link className="btn-secondary rel-botao" to={`/configuracao/minicursos?curso=${curso.id}&aba=etapas`}>
            <FiEdit2 aria-hidden="true" /> Editar curso
          </Link>
          <button type="button" className="btn-secondary rel-botao" onClick={exportar}>
            <FiDownload aria-hidden="true" /> Exportar Excel
          </button>
        </span>
      </div>

      <section className="rel-indicadores" aria-label="Resumo do curso">
        <Indicador rotulo="Inscritos" valor={resumo.inscritos} />
        <Indicador rotulo="Concluintes" valor={resumo.concluintes} apoio={`${pct(resumo.concluintes, resumo.inscritos)}% dos inscritos`} />
        <Indicador rotulo="Aprovação" valor={`${pct(resumo.aprovados, resumo.concluintes)}%`} apoio={`${resumo.aprovados} de ${resumo.concluintes} concluintes`} />
        <Indicador rotulo="Nota média" valor={formatarNota(resumo.notaMedia)} apoio="dos concluintes, de 0 a 10" />
        <Indicador rotulo="Acerto nas questões" valor={`${pct(resumo.acertosQuiz, resumo.respostasQuiz)}%`} apoio={`${resumo.respostasQuiz} respostas`} />
        <Indicador rotulo="Parados há +48h" valor={resumo.parados} apoio="sem concluir" />
      </section>

      {resumo.inscritos === 0 ? (
        <p className="qz-empty">Nenhum aluno neste curso ainda. Envie o comando {curso.comando} pelo WhatsApp para começar.</p>
      ) : (
        <>
          <section className="qz-card rel-secao">
            <h2 className="rel-secao__titulo">Funil de etapas</h2>
            <p className="rel-secao__apoio">Alunos que concluíram cada etapa. Uma queda forte mostra onde a turma para.</p>
            <Barras
              rotulo="Alunos que concluíram cada etapa"
              maximo={resumo.inscritos}
              itens={etapas.map((e, i) => {
                const valor = alunos.filter((p) => (p.ultimaEtapaConcluida || 0) >= i + 1).length;
                return {
                  chave: e.id,
                  prefixo: `${i + 1}. ${e.tipoConteudo === 'pilula' ? 'Pílula' : 'Questão'}`,
                  rotulo: tituloDaEtapa(e),
                  valor,
                  detalhe: `${valor} de ${resumo.inscritos} alunos (${pct(valor, resumo.inscritos)}%)`,
                };
              })}
            />
          </section>

          <section className="qz-card rel-secao">
            <h2 className="rel-secao__titulo">Questões</h2>
            <p className="rel-secao__apoio">Ordenadas da mais errada para a mais acertada.</p>
            <div className="rel-tabela-rolagem">
              <table className="rel-tabela">
                <thead>
                  <tr>
                    <th scope="col">Questão</th>
                    <th scope="col">Nível</th>
                    <th scope="col" className="rel-num">Respostas</th>
                    <th scope="col">Acerto</th>
                    <th scope="col">Erro mais comum</th>
                  </tr>
                </thead>
                <tbody>
                  {questoes.map((q) => (
                    <tr key={q.id}>
                      <td className="rel-tabela__texto">{q.enunciado}</td>
                      <td>{q.nivel}</td>
                      <td className="rel-num">{q.respostas}</td>
                      <td>
                        {q.respostas ? (
                          <span className="rel-celula-barra" title={`${q.acertos} de ${q.respostas} acertaram`}>
                            <span className="rel-celula-barra__trilho" aria-hidden="true">
                              <span className="rel-celula-barra__barra" style={{ '--p': q.taxa / 100 }} />
                            </span>
                            <span className="rel-num">{q.taxa}%</span>
                          </span>
                        ) : 'sem respostas'}
                      </td>
                      <td className="rel-tabela__texto rel-tabela__suave">{q.erroComum}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {enquetes.map((enquete) => (
            <section key={enquete.id} className="qz-card rel-secao">
              <h2 className="rel-secao__titulo">
                {enquete.tipo === 'multipla' ? 'Enquete' : 'Pergunta aberta'}: {enquete.enunciado}
              </h2>
              <p className="rel-secao__apoio">{enquete.total} {enquete.total === 1 ? 'resposta' : 'respostas'}</p>
              {enquete.total === 0 && <p className="rel-secao__apoio">Ainda sem respostas.</p>}
              {enquete.tipo === 'multipla' && enquete.total > 0 && (
                <Barras
                  rotulo={enquete.enunciado}
                  maximo={enquete.total}
                  formatarValor={(v) => `${v} · ${pct(v, enquete.total)}%`}
                  itens={enquete.itens.map((o) => ({ ...o, detalhe: `${o.valor} de ${enquete.total} respostas` }))}
                />
              )}
              {enquete.tipo === 'dissertativa' && enquete.total > 0 && (
                <>
                  <ul className="rel-citacoes">
                    {(verTodasDissertativas ? enquete.textos : enquete.textos.slice(0, DISSERTATIVAS_INICIAIS)).map((r) => (
                      <li key={r.id} className="rel-citacao">
                        <p className="rel-citacao__texto">{r.resposta}</p>
                        <span className="rel-citacao__autor">
                          {nomeDoTelefone[r.telefone] || formatarTelefone(r.telefone, r.idcontato)} · {tempoRelativo(r.timestamp)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {enquete.textos.length > DISSERTATIVAS_INICIAIS && (
                    <button type="button" className="btn-ghost rel-botao" onClick={() => setVerTodasDissertativas((v) => !v)}>
                      {verTodasDissertativas ? 'Mostrar menos' : `Ver todas as ${enquete.textos.length} respostas`}
                    </button>
                  )}
                </>
              )}
            </section>
          ))}

          <section className="qz-card rel-secao">
            <h2 className="rel-secao__titulo">Alunos</h2>
            <p className="rel-secao__apoio">Do mais recente para o mais antigo. Clique em um aluno para ver todas as respostas.</p>
            <div className="rel-tabela-rolagem">
              <table className="rel-tabela">
                <thead>
                  <tr>
                    <th scope="col">Aluno</th>
                    <th scope="col">Progresso</th>
                    <th scope="col" className="rel-num">Acertos</th>
                    <th scope="col" className="rel-num">Nota</th>
                    <th scope="col">Status</th>
                    <th scope="col">Última atividade</th>
                  </tr>
                </thead>
                <tbody>
                  {alunos.map((p) => {
                    const telefone = telefoneDoProgresso(p);
                    return (
                      <tr key={p.id}>
                        <td>
                          <button type="button" className="rel-link" onClick={() => onVerAluno(telefone)}>
                            {nomeDe(p) || 'Sem nome'}
                          </button>
                          <span className="rel-tabela__suave rel-bloco">{formatarTelefone(telefone, p.contactId)}</span>
                        </td>
                        <td><Medidor valor={p.ultimaEtapaConcluida || 0} total={totalEtapas} /></td>
                        <td className="rel-num">{p.acertos || 0}</td>
                        <td className="rel-num">{formatarNota(p.notaFinal)}</td>
                        <td><SeloStatus status={statusDoAluno(p)} /></td>
                        <td className="rel-tabela__suave">{tempoRelativo(p.timestampUltimaEtapa)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export default RelatorioCurso;
